/*
 * ContentContext
 *
 * Fetches /api/content/all once on mount and exposes the same named exports
 * that mock.js used to provide. Falls back to the seed values in mock.js so the
 * site renders instantly before the network round-trip.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import * as seed from "../mock";

const ContentContext = createContext(null);

// Map CMS collections onto the mock.js export names the components expect.
function shapeFromApi(api) {
  const settings = api?.settings || {};
  const brand = settings.brand || seed.BRAND;
  const videos = settings.videos || {};
  return {
    BRAND: {
      name: brand.name ?? seed.BRAND.name,
      tagline: brand.tagline ?? seed.BRAND.tagline,
      email: brand.email ?? seed.BRAND.email,
      locations: brand.locations ?? seed.BRAND.locations,
      copyright: brand.copyright ?? seed.BRAND.copyright,
    },
    NAV_LINKS: (api?.nav_links || seed.NAV_LINKS).map((n) => ({
      label: n.label,
      path: n.path,
    })),
    LATEST_RELEASES: api?.releases || seed.LATEST_RELEASES,
    FEATURED: (api?.releases && api.releases[0]) || seed.FEATURED,
    WORKS: api?.films || seed.WORKS,
    VIRAL_SOCIAL: api?.viral_social || seed.VIRAL_SOCIAL,
    PRINTS: api?.prints || seed.PRINTS,
    BRAND_LOGOS: api?.brands || seed.BRAND_LOGOS,
    MARQUEE_LOGOS: settings.marqueeLogos || seed.MARQUEE_LOGOS,
    TEAM: api?.team || seed.TEAM,
    CAPABILITIES: api?.capabilities || seed.CAPABILITIES,
    DISCIPLINES: (api?.disciplines || seed.DISCIPLINES.map((n) => ({ name: n })))
      .map((d) => (typeof d === "string" ? d : d.name)),
    ABOUT_COPY: settings.aboutCopy || seed.ABOUT_COPY,
    NEWSLETTER_COPY: settings.newsletterCopy || seed.NEWSLETTER_COPY,
    SOCIAL: settings.socials || seed.SOCIAL,
    HERO_STILLS: settings.heroStills || seed.HERO_STILLS,
    HERO_VIDEO: videos.hero || seed.HERO_VIDEO,
    WORK_HEADER_VIDEO: videos.workHeader || seed.WORK_HEADER_VIDEO,
    CAPABILITIES_HERO_VIDEO: videos.capabilities || seed.CAPABILITIES_HERO_VIDEO,
    ABOUT_HERO_VIDEO: videos.about || seed.ABOUT_HERO_VIDEO,
    HYBRID_VIDEO: videos.hybrid || seed.HYBRID_VIDEO,
    AI_LED_VIDEO: videos.aiLed || seed.AI_LED_VIDEO,
    CONTENT_ENGINE_VIDEO: videos.contentEngine || seed.CONTENT_ENGINE_VIDEO,
  };
}

// Static fallback derived from mock.js (used immediately on first render).
const seedShape = {
  BRAND: seed.BRAND,
  NAV_LINKS: seed.NAV_LINKS,
  LATEST_RELEASES: seed.LATEST_RELEASES,
  FEATURED: seed.FEATURED,
  WORKS: seed.WORKS,
  VIRAL_SOCIAL: seed.VIRAL_SOCIAL,
  PRINTS: seed.PRINTS,
  BRAND_LOGOS: seed.BRAND_LOGOS,
  MARQUEE_LOGOS: seed.MARQUEE_LOGOS,
  TEAM: seed.TEAM,
  CAPABILITIES: seed.CAPABILITIES,
  DISCIPLINES: seed.DISCIPLINES,
  ABOUT_COPY: seed.ABOUT_COPY,
  NEWSLETTER_COPY: seed.NEWSLETTER_COPY,
  SOCIAL: seed.SOCIAL,
  HERO_STILLS: seed.HERO_STILLS,
  HERO_VIDEO: seed.HERO_VIDEO,
  WORK_HEADER_VIDEO: seed.WORK_HEADER_VIDEO,
  CAPABILITIES_HERO_VIDEO: seed.CAPABILITIES_HERO_VIDEO,
  ABOUT_HERO_VIDEO: seed.ABOUT_HERO_VIDEO,
  HYBRID_VIDEO: seed.HYBRID_VIDEO,
  AI_LED_VIDEO: seed.AI_LED_VIDEO,
  CONTENT_ENGINE_VIDEO: seed.CONTENT_ENGINE_VIDEO,
};

export function ContentProvider({ children }) {
  const [state, setState] = useState({ ...seedShape, _hydrated: false });

  const refetch = React.useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/all`, {
        headers: { "Cache-Control": "no-store" },
      });
      if (!res.ok) throw new Error(`content/all ${res.status}`);
      const api = await res.json();
      setState({ ...shapeFromApi(api), _hydrated: true });
    } catch (err) {
      // Silent: keep the seed values so the site never breaks.
      // eslint-disable-next-line no-console
      console.warn("[content] hydration failed, using seed", err);
      setState((s) => ({ ...s, _hydrated: true }));
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const value = useMemo(() => ({ ...state, refetch }), [state, refetch]);
  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) {
    throw new Error("useContent must be used within ContentProvider");
  }
  return ctx;
}
